import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { CheckCircle2, Lightbulb } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface Annotation {
  type: 'arrow' | 'highlight' | 'box';
  x: number;
  y: number;
  width?: number;
  height?: number;
  label: string;
}

interface StepCardProps {
  stepNumber: number;
  title: string;
  description: string;
  screenshot: string;
  annotations?: Annotation[];
  instructions: string[];
  tips?: string[];
  isCompleted?: boolean;
}

export function StepCard({
  stepNumber,
  title,
  description,
  screenshot,
  instructions,
  tips,
  isCompleted = false,
}: StepCardProps) {
  const [isZoomed, setIsZoomed] = useState(false);

  return (
    <>
      <Card className="mb-8 overflow-hidden border-2 hover:border-primary/50 transition-colors">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-transparent">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <Badge variant="default" className="h-8 w-8 rounded-full flex items-center justify-center text-base">
                {stepNumber}
              </Badge>
              <div>
                <CardTitle className="text-2xl">{title}</CardTitle>
                <CardDescription className="text-base mt-1">{description}</CardDescription>
              </div>
            </div>
            {isCompleted && (
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            )}
          </div>
        </CardHeader>
        
        <CardContent className="pt-6 space-y-6">
          {/* Screenshot */}
          <div 
            className="cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => setIsZoomed(true)}
          >
            <AspectRatio ratio={16 / 9} className="bg-muted rounded-lg overflow-hidden border">
              <img
                src={screenshot}
                alt={`${title} screenshot`}
                className="object-cover w-full h-full"
              />
            </AspectRatio>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Click to view full size
            </p>
          </div>

          {/* Instructions */}
          <div>
            <h4 className="font-semibold text-lg mb-3">Step-by-Step Instructions:</h4>
            <ol className="space-y-2">
              {instructions.map((instruction, index) => (
                <li key={index} className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </span>
                  <span className="text-base leading-relaxed">{instruction}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Tips */}
          {tips && tips.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Lightbulb className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">Pro Tips:</h5>
                  <ul className="space-y-1 text-sm text-amber-800 dark:text-amber-200">
                    {tips.map((tip, index) => (
                      <li key={index} className="flex gap-2">
                        <span>•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Zoom Dialog */}
      <Dialog open={isZoomed} onOpenChange={setIsZoomed}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-2">
          <img
            src={screenshot}
            alt={`${title} screenshot - full size`}
            className="w-full h-full object-contain"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
